// Basic
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsOrganizations from '@/app/modules/organizations/api/actionsOrganizations';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
// Common
import SIM_OFFERS_PROVIDERS from '@/app/modules/sims/enums/SimOffersProviders';
// Lang
import { nsOrganization, nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.organizationId
 * @param root0.selectedProvider
 * @param root0.setSelectedProvider
 * @param root0.selectedOffer
 * @param root0.setSelectedOffer
 * @param root0.setSelectedOfferName
 * @param root0.isOfferActive
 * @returns {JSX} SelectProviderAndOffer
 */
function SelectProviderAndOffer({
    organizationId,
    selectedProvider,
    setSelectedProvider,
    selectedOffer = null,
    setSelectedOffer = null,
    setSelectedOfferName = null,
    isOfferActive = true
}) {

    // Api calls
    const { data: providersData, isFetching } = actionsOrganizations.getOrganizationSimOffers({ organization: organizationId, isOfferActive: isOfferActive });

    // Functions
    const getSimProviders = () => {
        const simProviders = providersData ? Object.keys(providersData) : [];

        return Object.values(SIM_OFFERS_PROVIDERS)
            .filter(item => simProviders.includes(item.name))
            .map((item) => ({ value: item.key, name: item.entities.name, namespace: nsOrganization }));
    };
    const getOffers = () => {
        const selectedProviderName = Object.values(SIM_OFFERS_PROVIDERS).find((item) => item.key === selectedProvider)?.name;

        if (!providersData || !providersData[selectedProviderName]) { return []; }

        return providersData[selectedProviderName]
            .map((item) => ({ value: item.uuid, name: item.displayName }));
    };

    // Memoized values
    const simProviders = useMemo(() => getSimProviders(), [providersData]);
    const offersList = useMemo(() => getOffers(), [selectedProvider, providersData]);

    // Hooks
    useEffect(() => {
        // Has only one provider
        if (providersData && Object.keys(providersData).length === 1) {
            setSelectedProvider(simProviders[0].value);
        }
    }, [providersData]);

    return (
        <div className='my-4'>
            {
                isFetching && <Spinner className='mt-4' />
            }
            {
                !isFetching && providersData &&
                <>
                    <div>
                        <Entity entity='modal.multiple_activation.provider.label' namespace={nsSimCard} />
                    </div>
                    <Select
                        value={selectedProvider}
                        options={simProviders}
                        onChange={(value) => setSelectedProvider(value)}
                        namespace={nsSimCard}
                    />
                    {
                        selectedOffer !== null && setSelectedOffer !== null && setSelectedOfferName !== null &&
                        <>
                            <div>
                                <Entity entity='modal.multiple_activation.offer.label' namespace={nsSimCard} />
                            </div>
                            <Select
                                value={selectedOffer}
                                options={offersList}
                                onChange={(value) => {
                                    setSelectedOffer(value);
                                    setSelectedOfferName(offersList.find((item) => item.value === value).name);
                                }}
                                namespace={nsOrganization}
                            />
                        </>
                    }
                </>
            }
        </div >
    );
}

SelectProviderAndOffer.propTypes = {
    organizationId: PropTypes.string,
    selectedProvider: PropTypes.string,
    setSelectedProvider: PropTypes.func,
    selectedOffer: PropTypes.string,
    setSelectedOffer: PropTypes.func,
    setSelectedOfferName: PropTypes.func,
    isOfferActive: PropTypes.bool
};
export default SelectProviderAndOffer;